import { Accordion, AccordionButton, AccordionItem, AccordionPanel, Divider, Flex, Heading } from "@chakra-ui/react";
import { useIsMobile } from "@equidefi/hooks";
import { formatDateValue } from "@equidefi/i18n";
import { offeringHidesSecurityLabel } from "@equidefi/shared";
import type { DTO } from "@equidefi/shared/types/dto";
import { Text } from "@equidefi/ui";
import { Icon } from "@equidefi/ui/icon";
import type React from "react";

import { CurrencyFormatter, NumberFormatter } from "@/constants/format";

const DetailRow: React.FC<{ label: React.ReactNode; value: React.ReactNode }> = ({ label, value }) => (
  <Flex justify="space-between" align="center" py={2}>
    <Text fontSize={{ base: "sm", md: "md" }} m={0}>
      {label}
    </Text>
    <Text fontSize={{ base: "sm", md: "md" }} textAlign="right" m={0}>
      {value}
    </Text>
  </Flex>
);

interface OfferingDetailsCardProps {
  offering: DTO.Offering | null | undefined;
  issuer: DTO.Issuer | null | undefined;
}

export const OfferingDetailsCard: React.FC<OfferingDetailsCardProps> = ({ offering, issuer }) => {
  const hideSecurityLabel = offeringHidesSecurityLabel(offering);
  // Expanded by default on desktop, collapsed on mobile.
  const defaultIndex = useIsMobile("lg") ? [] : [0];

  if (!offering) return null;

  return (
    <Accordion allowToggle defaultIndex={defaultIndex}>
      <AccordionItem
        border="1px solid"
        borderColor="gray.200"
        bg="white"
        borderRadius={{ base: "10px", md: "12px" }}
        boxShadow="card"
        px={{ base: 4, md: 5 }}>
        {({ isExpanded }) => (
          <>
            <AccordionButton px="0" py={{ base: 4, md: 5 }} _hover={{ bg: "transparent" }}>
              <Heading
                flex="1"
                textAlign="left"
                m="0"
                fontWeight="bold"
                color="#171c2c"
                fontSize={{ base: "md", md: "lg" }}>
                Offering Details
              </Heading>
              <Flex align="center" gap="1" color="equidefi.blue" fontWeight="semibold" fontSize="sm">
                <Text m="0" textDecoration="underline">
                  {isExpanded ? "Collapse Details" : "Expand Details"}
                </Text>
                {isExpanded ? <Icon.ChevronUp size="1em" /> : <Icon.ChevronDown size="1em" />}
              </Flex>
            </AccordionButton>

            <AccordionPanel px="0" pt="0" pb={{ base: 4, md: 5 }}>
              <Heading m="0" mb="1" fontWeight="bold" fontSize={{ base: "sm", md: "md" }} color="#171c2c">
                {issuer?.name}
              </Heading>
              {offering.name && (
                <Text m="0" fontSize={{ base: "sm", md: "md" }}>
                  {offering.name}
                </Text>
              )}

              <Divider my="3" />

              <DetailRow label="Total Amount" value={CurrencyFormatter.format(Number(offering.total_amount ?? 0))} />
              {!hideSecurityLabel && (
                <DetailRow
                  label={`Total ${offering.security_type_plural}`}
                  value={NumberFormatter.format(Number(offering.total_units ?? 0))}
                />
              )}

              <Divider my="3" />

              <DetailRow label="Offering Start Date" value={formatDateValue(offering.launch_date) || "-"} />
              <DetailRow label="Offering End Date" value={formatDateValue(offering.termination_date) || "-"} />
            </AccordionPanel>
          </>
        )}
      </AccordionItem>
    </Accordion>
  );
};

export default OfferingDetailsCard;
